import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Loader2,
  Paperclip,
  X,
  Clock,
  ShieldCheck,
  ClipboardCheck,
  BadgeCheck,
} from "lucide-react";
import { API } from "@/lib/api";
import axios from "axios";

const SITE_TYPES = [
  "Commercial Office",
  "Industrial",
  "Warehouse",
  "Food Factory",
  "Cleanroom",
  "Hospital",
  "Medical Device",
  "Retail / Leisure / Shopping Centre",
  "Post-Construction",
  "School / University",
  "Other",
];

const ACCEPTED_EXTS = [
  // Documents
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".xlsm", ".csv", ".txt", ".rtf",
  // Images
  ".jpg", ".jpeg", ".png", ".gif", ".webp", ".heic", ".heif", ".tiff", ".bmp",
  // Video
  ".mp4", ".mov", ".m4v", ".webm", ".avi", ".mkv",
  // Audio
  ".mp3", ".wav", ".m4a", ".ogg",
  // Archives
  ".zip", ".rar", ".7z",
];
const ACCEPT_ATTR = ACCEPTED_EXTS.join(",");
const MAX_BYTES = 200 * 1024 * 1024;

const FREQUENCIES = [
  { value: "One-off / Single clean", group: "One-off" },
  { value: "Daily (recurring)", group: "Recurring" },
  { value: "Weekly (recurring)", group: "Recurring" },
  { value: "Fortnightly (recurring)", group: "Recurring" },
  { value: "Monthly (recurring)", group: "Recurring" },
  { value: "Quarterly deep-clean", group: "Recurring" },
  { value: "Not sure — please advise", group: "Other" },
];

const initial = {
  company_name: "",
  contact_person: "",
  email: "",
  phone: "",
  site_type: SITE_TYPES[0],
  frequency: FREQUENCIES[1].value,
  message: "",
};

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

export default function QuoteForm({ initialSiteType, hideHeader = false, hideFrequency = true }) {
  const [form, setForm] = useState({ ...initial, ...(initialSiteType ? { site_type: initialSiteType } : {}) });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = `.${f.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ACCEPTED_EXTS.join(", ")}`);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("File too large (max 200 MB).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.company_name || !form.contact_person || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("attachment", file);
      await axios.post(`${API}/quote-requests`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSent(true);
      setForm(initial);
      clearFile();
      toast.success("Request received — we'll reply within 24 hours.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not send your request. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send your request.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-ags-hairline rounded-none px-4 py-3.5 text-[#0A1F40] text-base placeholder:text-[#A3A3A3] focus:border-[#0E2A57] focus:ring-1 focus:ring-[#0E2A57] focus:outline-none transition";

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="bg-white py-28 md:py-40 border-t border-ags-hairline"
    >
      <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-16 bg-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              11 — Request a Quote
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-4xl md:text-6xl leading-[1.0] tracking-[-0.04em] text-ags-ink">
            Request a
            <br />
            <span className="italic-display font-normal text-[#0E2A57]">
              24-hour
            </span>{" "}
            quotation.
          </h2>
          <p className="mt-8 text-ags-text-muted text-lg leading-relaxed max-w-md">
            Tell us about your premises. Attach floor plans, tender docs, or
            photos — we'll review and send a transparent, itemised proposal.
          </p>

          <div className="mt-10 space-y-4 text-sm text-ags-text-muted">
            <div className="flex gap-3">
              <span className="text-[#2BB6E5] font-bold">→</span>
              <span>Quotation within 24 hours (business days)</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[#2BB6E5] font-bold">→</span>
              <span>Nationwide coverage across Ireland</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[#2BB6E5] font-bold">→</span>
              <span>Fully insured · RAMS-compliant · Trained operatives</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="quote-form"
            className="bg-ags-cream border border-ags-hairline shadow-ags-ambient p-6 md:p-10"
            encType="multipart/form-data"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Company Name *">
                <input
                  name="company_name"
                  value={form.company_name}
                  onChange={onChange}
                  required
                  placeholder="Acme Logistics Ltd."
                  data-testid="quote-input-company"
                  className={inputClass}
                />
              </Field>
              <Field label="Contact Person *">
                <input
                  name="contact_person"
                  value={form.contact_person}
                  onChange={onChange}
                  required
                  placeholder="Jane Murphy"
                  data-testid="quote-input-contact"
                  className={inputClass}
                />
              </Field>
              <Field label="Email *">
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder="jane@company.ie"
                  data-testid="quote-input-email"
                  className={inputClass}
                />
              </Field>
              <Field label="Phone *">
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  placeholder="+353 1 234 5678"
                  data-testid="quote-input-phone"
                  className={inputClass}
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Site Type *">
                  <select
                    name="site_type"
                    value={form.site_type}
                    onChange={onChange}
                    data-testid="quote-input-site-type"
                    className={inputClass}
                  >
                    {SITE_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {!hideFrequency && (
                <div className="md:col-span-2">
                  <Field label="Cleaning Frequency *">
                    <div
                      data-testid="quote-frequency-group"
                      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
                    >
                    {FREQUENCIES.map((f) => {
                      const active = form.frequency === f.value;
                      return (
                        <button
                          type="button"
                          key={f.value}
                          data-testid={`quote-frequency-${f.value}`}
                          onClick={() => setForm((s) => ({ ...s, frequency: f.value }))}
                          className={`text-left px-3 py-3 border transition-colors text-xs leading-tight ${
                            active
                              ? "bg-[#0E2A57] text-white border-[#0E2A57]"
                              : "bg-white text-[#0E2A57] border-[#E5EBF2] hover:border-[#0E2A57]/40"
                          }`}
                        >
                          <span className={`block text-[9px] uppercase tracking-[0.18em] font-semibold mb-0.5 ${active ? "text-[#BEE7F5]" : "text-[#2BB6E5]"}`}>
                            {f.group}
                          </span>
                          <span className="font-semibold">{f.value.replace(/ \(recurring\)/, "")}</span>
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>
              )}
              <div className="md:col-span-2">
                <Field label="Message">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    placeholder="Tell us about your premises, square footage, current schedule, or specific requirements…"
                    data-testid="quote-input-message"
                    className={`${inputClass} resize-none`}
                  />
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field label="Attach a File (optional)">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_ATTR}
                    onChange={onFile}
                    data-testid="quote-input-file"
                    className="hidden"
                  />
                  {!file ? (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="quote-file-trigger"
                      className="w-full bg-white border-2 border-dashed border-[#0E2A57]/25 hover:border-[#0E2A57]/70 hover:bg-ags-sand-soft/60 px-6 py-8 md:py-10 flex flex-col sm:flex-row items-center sm:items-center sm:justify-between gap-5 transition-[background-color,border-color,transform] duration-300 text-left group hover:-translate-y-0.5"
                    >
                      <div className="flex items-center gap-4">
                        <span className="bg-[#0E2A57] group-hover:bg-[#163B75] text-white w-12 h-12 flex items-center justify-center transition-colors">
                          <Paperclip size={18} strokeWidth={1.75} />
                        </span>
                        <div>
                          <div className="font-heading font-bold text-base md:text-[17px] text-[#0E2A57] tracking-[-0.01em]">
                            Upload one file
                          </div>
                          <div className="text-[11px] md:text-xs text-ags-text-muted mt-1 font-mono uppercase tracking-[0.18em]">
                            Documents · Images · Video · Audio · max 200 MB
                          </div>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0E2A57] font-bold opacity-70 group-hover:opacity-100 group-hover:text-[#163B75] transition-opacity">
                        Browse
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </span>
                    </button>
                  ) : (
                    <div
                      data-testid="quote-file-preview"
                      className="w-full bg-white border border-[#0E2A57]/40 shadow-ags-ambient px-5 py-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="bg-[#2BB6E5] text-[#0E2A57] w-11 h-11 flex items-center justify-center font-mono font-bold text-[11px] tracking-wider">
                          {file.name.split(".").pop()?.toUpperCase().slice(0, 4)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-[#0E2A57] truncate">
                            {file.name}
                          </div>
                          <div className="text-xs text-ags-text-muted mt-0.5 font-mono uppercase tracking-[0.18em]">
                            {formatBytes(file.size)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearFile}
                        data-testid="quote-file-clear"
                        className="text-ags-text-muted hover:text-[#0E2A57] p-2 transition-colors"
                        aria-label="Remove file"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </Field>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <p className="text-xs text-ags-text-muted max-w-sm">
                By submitting, you agree we may contact you about your enquiry.
                Your information will only be used to respond to your enquiry.
                We will never share your details.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#163B75] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-5 font-semibold tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-ags-ambient hover:shadow-ags-hover"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Request
                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>

            {/* Trust reassurance under submit */}
            <div
              data-testid="quote-trust-icons"
              className="mt-6 pt-6 border-t border-ags-hairline grid grid-cols-2 md:grid-cols-4 gap-3"
            >
              {[
                { icon: Clock,           label: "Response within 24h" },
                { icon: ClipboardCheck,  label: "No obligation" },
                { icon: BadgeCheck,      label: "Free site survey" },
                { icon: ShieldCheck,     label: "Fully insured" },
              ].map(({ icon: I, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2.5 text-[#0E2A57]"
                >
                  <span className="w-8 h-8 bg-white border border-ags-hairline flex items-center justify-center flex-shrink-0">
                    <I size={14} strokeWidth={1.75} className="text-[#2BB6E5]" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold leading-tight">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {sent && (
              <div
                data-testid="quote-success"
                className="mt-6 border border-[#0E2A57]/30 bg-[#BEE7F5]/30 text-[#081A37] p-4 text-sm"
              >
                Thank you — your request has been received. A member of the AGS
                family will reply within 24 hours.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 font-medium mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
